import { EventType, KnownMembership, Preset, RoomType } from 'matrix-js-sdk/src/matrix';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useBotJoinGate from 'hooks/useBotJoinGate/useBotJoinGate';
import useCreateRoom from 'hooks/useCreateRoom/useCreateRoom';
import { getViaServers, MEEDIO_BOT_ID } from 'utils/matrixUtils';
import { logAndSendToSentry } from 'utils/utils';

import {
  buildWorkspaceInitialStates,
  DEFAULT_OWNER_ROLE_POWER_LEVEL,
  waitForRoleSync,
  workspacePowerLevelOverrides,
} from './utils';

export type CreateWorkspacePayload = {
  name: string;
  alias?: string;
  parentWorkspaceId: string | null;
  onCreated?: (roomId: string) => void;
};

const useCreateWorkspace = () => {
  const { createRoom } = useCreateRoom();
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const { t } = useTranslation();
  const { waitForBotJoin, reset: resetBotJoinGate } = useBotJoinGate();
  const [isLoading, setIsLoading] = useState(false);
  const [isWaitingForBot, setIsWaitingForBot] = useState(false);
  const [pendingBotRetryRoomId, setPendingBotRetryRoomId] = useState<string | null>(null);

  // Decision 24 / AC-280 / AC-286: bot-join is the completion signal for
  // workspace creation. After `createRoom` resolves and any `m.space.child`
  // link is written, the hook waits up to `BOT_JOIN_TIMEOUT_MS` (30 s) for the
  // bot to actually join the workspace room. `onCreated` is invoked **only**
  // on success (AC-281, AC-290). Timeout surfaces a retry affordance that
  // re-invites the bot if needed and re-runs the gate on the same room
  // (AC-282, AC-284) — no second `createRoom`.
  const awaitBotJoinForRoom = useCallback(
    async (roomId: string, onCreated?: (id: string) => void): Promise<boolean> => {
      setIsWaitingForBot(true);
      try {
        await waitForBotJoin(roomId);
        setPendingBotRetryRoomId(null);
        if (onCreated) onCreated(roomId);
        await waitForRoleSync(matrixClient, roomId);
        return true;
      } catch {
        setPendingBotRetryRoomId(roomId);
        pushToast({ variant: 'error', title: t('workspaces.bot_join_timeout') });
        return false;
      } finally {
        setIsWaitingForBot(false);
      }
    },
    [matrixClient, pushToast, t, waitForBotJoin]
  );

  const createWorkspace = useCallback(
    async ({ name, alias, parentWorkspaceId, onCreated }: CreateWorkspacePayload) => {
      setIsLoading(true);

      let workspaceId: string | null = null;
      const workspaceInitialStates = buildWorkspaceInitialStates(name, matrixClient, parentWorkspaceId, alias);

      try {
        const space = await createRoom({
          title: name,
          alias,
          customInitialStates: workspaceInitialStates,
          customCreationContent: { type: RoomType.Space },
          preset: Preset.PrivateChat,
          powerLevelOverrides: workspacePowerLevelOverrides,
          includeBot: true,
          ownerPowerLevel: DEFAULT_OWNER_ROLE_POWER_LEVEL,
        });

        workspaceId = space.room_id;

        const currentUserId = matrixClient.getUserId();
        if (!currentUserId) throw new Error('Failed to get current user id when creating a workspace');

        if (parentWorkspaceId && space) {
          const viaServers = getViaServers(parentWorkspaceId, matrixClient);

          await matrixClient.sendStateEvent(parentWorkspaceId, EventType.SpaceChild, { via: viaServers }, workspaceId);
        }

        const ok = await awaitBotJoinForRoom(space.room_id, onCreated);
        if (!ok) return null;

        return space;
      } catch (error) {
        if (workspaceId) {
          await matrixClient.leave(workspaceId);
          await matrixClient.forget(workspaceId);
        }

        logAndSendToSentry('Failed to create workspace', { error });
        pushToast({ variant: 'error', title: t('workspaces.workspace_creation_error') });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [awaitBotJoinForRoom, createRoom, matrixClient, pushToast, t]
  );

  // AC-284 retry — re-invite the bot if its membership is missing / `'leave'`
  // and re-run the bot-join gate on the same workspace room. The `createRoom`
  // step is NOT re-run; only the bot interaction is retried.
  const retryBotJoin = useCallback(
    async (onCreated?: (id: string) => void): Promise<boolean> => {
      const roomId = pendingBotRetryRoomId;
      if (!roomId || !MEEDIO_BOT_ID) return false;

      try {
        const room = matrixClient.getRoom(roomId);
        const botMembership = room?.getMember(MEEDIO_BOT_ID)?.membership;
        if (botMembership !== KnownMembership.Join && botMembership !== KnownMembership.Invite) {
          await matrixClient.invite(roomId, MEEDIO_BOT_ID);
        }
      } catch (error) {
        logAndSendToSentry('Failed to re-invite Meedio bot during workspace creation retry', { error });
      }

      resetBotJoinGate();
      return awaitBotJoinForRoom(roomId, onCreated);
    },
    [awaitBotJoinForRoom, matrixClient, pendingBotRetryRoomId, resetBotJoinGate]
  );

  return {
    createWorkspace,
    isCreatingWorkspace: isLoading,
    isWaitingForBot,
    pendingBotRetryRoomId,
    retryBotJoin,
  };
};

export default useCreateWorkspace;
